// chat/actions.ts — the chat control EXECUTOR for Oriora Plant Diary (cloned from Oriora
// Expense's src/chat/actions.ts, adapted to THIS app's own store). The client router
// (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that action
// against the on-device store (src/store.ts).
//
// Every ChatAction is derived from what src/store.ts ACTUALLY supports:
//   • add_plant     → store.createPlant
//   • log_care      → store.createEvent (water | feed | repot | prune | note)
//   • edit_plant    → store.updatePlant  (the store HAS an update command → editing is exposed)
//   • delete_plant  → store.deletePlant  (destructive → UI holds it for an explicit Confirm tap)
//   • filter        → store.listPlants   (LOCAL search — no server, no billing)
//   • recap         → the app's EXISTING live brain endpoints: narrateThisWeek / narratePlantStory
//
// All mutations are LOCAL SQLite over Tauri IPC. The ONE billed action is `recap`, which reuses the
// app's ALREADY-LIVE narration endpoints (no new/edited server routes). It only fires when the user
// explicitly asks for a recap/story — idle chit-chat never bills.
//
// HITL discipline (mirrors Expense): delete_plant is NEVER executed from a chat turn on its own —
// the Chat UI holds it and requires an explicit Confirm tap first. This executor just performs the
// delete once the UI hands it back.

import { store, todayLocal, kindEmoji, kindLabel, type CareKind, type PlantPatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "plant-diary";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, saveToCloud } from "../brain";
import { addInstruction } from "./brain";
import { narrateThisWeek, narratePlantStory, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact plant row rendered inline in a chat bubble (filter/search results).
export interface PlantRow {
  id: string;
  name: string;
  species: string;
  location: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_plant"; name: string; species?: string; location?: string; potted_on?: string | null }
  | { kind: "log_care"; plant_id: string; plant_name: string; care: CareKind; done_on?: string; note?: string }
  | { kind: "edit_plant"; id: string; patch: PlantPatch; label?: string }
  | { kind: "delete_plant"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "delete_event"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "delete_growth"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "save_to_cloud"; subject: "note" | "conversation"; name: string; content?: string; provider?: "github" | "gdrive" }
  | { kind: "filter"; term: string; label?: string }
  | { kind: "log_growth"; plant_id: string; plant_name: string; height_cm?: number | null; leaf_count?: number | null; measured_on?: string; note?: string }
  | { kind: "recap"; plant_id?: string; plant_name?: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the plants/care log changed → the collection should refresh
  rows?: PlantRow[];  // search matches to render inline
}

/** Extra context the CHAT holds and the action layer can't reach on its own (e.g. the transcript). */
export interface ActionContext { transcript?: string }

export async function executeAction(a: ChatAction, ctx: ActionContext = {}): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_plant": {
        // Mirror PlantForm.save: pass the full shape (empty strings + null potted_on) the store expects.
        const p = await store.createPlant({
          name: a.name,
          species: a.species ?? "",
          location: a.location ?? "",
          potted_on: a.potted_on ?? null,
        });
        const extra = [p.species && `species ${p.species}`, p.location && `in ${p.location}`].filter(Boolean).join(", ");
        return { ok: true, message: `Added ${p.name}${extra ? ` (${extra})` : ""}.`, changed: true };
      }

      case "log_care": {
        await store.createEvent({
          plant_id: a.plant_id,
          kind: a.care,
          done_on: a.done_on ?? todayLocal(),
          note: a.note ?? "",
        });
        const when = a.done_on && a.done_on !== todayLocal() ? ` on ${a.done_on}` : "";
        return { ok: true, message: `${kindEmoji(a.care)} Logged "${kindLabel(a.care)}" for ${a.plant_name}${when}.`, changed: true };
      }

      case "log_growth": {
        // The Rust guard rejects an entry with neither number; surface that plainly rather
        // than letting the chat claim it recorded something it didn't.
        if (a.height_cm == null && a.leaf_count == null) {
          return { ok: false, message: "Tell me a height (cm) or a leaf count and I'll record it." };
        }
        await store.createGrowth({
          plant_id: a.plant_id,
          measured_on: a.measured_on ?? todayLocal(),
          height_cm: a.height_cm ?? null,
          leaf_count: a.leaf_count ?? null,
          note: a.note ?? "",
        });
        const parts = [
          a.height_cm != null ? `${a.height_cm} cm` : null,
          a.leaf_count != null ? `${a.leaf_count} leaves` : null,
        ].filter(Boolean).join(" · ");
        return { ok: true, message: `🌱 Measured ${a.plant_name}: ${parts}.`, changed: true };
      }

      case "edit_plant":
        await store.updatePlant(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the plant"}.`, changed: true };

      case "delete_plant":
        await store.deletePlant(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the plant"} and its care log.`, changed: true };

      // The UI can delete a single timeline entry, so the chat must be able to as well
      // (Pattern 6 capability-completeness). Confirm-gated in Chat.tsx, like delete_plant.
      case "delete_event":
        await store.deleteEvent(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "that entry"}.`, changed: true };

      case "delete_growth":
        await store.deleteGrowth(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "that measurement"}.`, changed: true };

      // Tier A: save anything they ask to THEIR OWN cloud — a note, or this whole conversation.
      case "save_to_cloud": {
        const content = a.subject === "conversation" ? (ctx.transcript ?? "") : (a.content ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.subject === "conversation" ? "There's nothing in this conversation yet." : "What would you like me to save?" };
        }
        const { provider } = await saveToCloud(a.name, content, a.subject, a.provider);
        const where = provider === "gdrive" ? "your Google Drive" : provider === "github" ? "your GitHub" : "your cloud";
        return { ok: true, message: `Saved ${a.subject === "conversation" ? "this conversation" : `"${a.name}"`} to ${where}.` };
      }

      case "filter": {
        const all = await store.listPlants();
        const q = a.term.trim().toLowerCase();
        const rows = q
          ? all.filter((p) =>
              p.name.toLowerCase().includes(q) ||
              p.species.toLowerCase().includes(q) ||
              p.location.toLowerCase().includes(q))
          : all;
        const rendered: PlantRow[] = rows.slice(0, 50).map((p) => ({
          id: p.id, name: p.name, species: p.species, location: p.location,
        }));
        const forLabel = a.label && a.label !== "all" ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${rows.length} plant${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "recap": {
        // The one billed step — reuses the app's EXISTING live endpoints (no new routes).
        const today = todayLocal();
        if (a.plant_id) {
          const signals = await store.computePlantSignals(a.plant_id, today);
          const n = await narratePlantStory(signals);
          return { ok: true, message: n.text || "Nothing to narrate yet." };
        }
        const signals = await store.computeWeekSignals(today);
        const n = await narrateThisWeek(signals);
        return { ok: true, message: n.text || "Nothing to narrate yet." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my data" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: this app's backup can exceed the relay's read ceiling (photos).
          // If GitHub is connected on this device, chat pulls it directly itself (the user already
          // confirmed the REPLACE before this executes); it can't run the one-time OAuth login,
          // so when not connected it points at the button by name instead — never a dead end.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    // BrainError carries a user-ready message (insufficient funds / offline / refused) — surface it as-is.
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
